/** AdminVouchers - platform-wide gift/promo voucher browser (search, filters, CSV). */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams } from 'expo-router';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { titleCase } from '../../src/utils/format';

interface VoucherItem {
  id: string;
  code: string;
  kind: string;
  source: string;
  status: string;
  offer: string;
  recipientEmail: string | null;
  redemptionsUsed: number;
  redemptionLimit: number;
  createdAt: string;
  expiresAt: string | null;
  ownerName: string | null;
  attendantName: string | null;
}

const STATUS_FILTERS = [
  { label: 'Active', value: 'active' },
  { label: 'Redeemed', value: 'redeemed' },
  { label: 'Expired', value: 'expired' },
  { label: 'Revoked', value: 'revoked' },
];

export default function AdminVouchers() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [rows, setRows] = useState<VoucherItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { search: searchParam, ownerId: paramOwnerId } = useLocalSearchParams<{ search?: string; ownerId?: string }>();
  const ownerFilter = typeof paramOwnerId === 'string' ? paramOwnerId : '';
  const [ownerFilterName, setOwnerFilterName] = useState('');

  const fetchRows = useCallback(async (p = 1, s = '', status = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (status) params.set('status', status);
      if (ownerFilter) params.set('ownerId', ownerFilter);
      const data = await api<{ vouchers: VoucherItem[]; total: number }>(`/api/admin/vouchers?${params}`);
      if (signal?.aborted) return;
      setRows(data.vouchers);
      setTotal(data.total);
      setPage(p);
      if (ownerFilter) setOwnerFilterName(data.vouchers.find(v => (v as any).ownerName)?.ownerName ?? '');
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [ownerFilter]);

  useEffect(() => {
    const ac = new AbortController();
    const initial = typeof searchParam === 'string' ? searchParam : '';
    if (initial) setSearch(initial);
    fetchRows(1, initial, '', ac.signal);
    return () => ac.abort();
  }, [searchParam, fetchRows]);

  const statusColor = (s: string) =>
    s === 'active' ? colors.success : s === 'redeemed' ? colors.primary : colors.textLight;

  const csvParams = new URLSearchParams({ ...(search && { search }), ...(statusFilter && { status: statusFilter }), ...(ownerFilter && { ownerId: ownerFilter }) });

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
        {ownerFilter !== '' && (
          <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 6, backgroundColor: colors.primary + '15', borderWidth: 1, borderColor: colors.primary, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10 }}>
            <Ionicons name="business" size={14} color={colors.primary} />
            <Text style={{ color: colors.primary, fontSize: 13, fontWeight: '600' }}>
              {ownerFilterName ? `Owner: ${ownerFilterName}` : 'Filtered to one owner'}
            </Text>
          </View>
        )}
        <AdminToolbar
          search={search} onSearchChange={setSearch} onSearch={() => fetchRows(1, search, statusFilter)}
          total={total} page={page} limit={20} onPageChange={(p) => fetchRows(p, search, statusFilter)}
          csvEndpoint={`/api/admin/vouchers?${csvParams}`}
          csvFilename="vouchers.csv" label="voucher"
          filterOptions={STATUS_FILTERS} activeFilter={statusFilter}
          onFilterChange={(v) => { setStatusFilter(v); fetchRows(1, search, v); }}
        />
        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : (
          <FlatList ListFooterComponent={<PageFooter />}
            data={rows}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <View style={styles.row}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.offer}>{item.offer}</Text>
                    <Text style={[styles.badge, { color: statusColor(item.status) }]}>{item.status}</Text>
                  </View>
                  <Text style={styles.meta} numberOfLines={1}>
                    {item.ownerName || '?'} · {item.code} · {item.kind}/{item.source}
                  </Text>
                  <Text style={styles.sub} numberOfLines={2}>
                    {item.recipientEmail ? `To ${item.recipientEmail} · ` : ''}
                    {item.attendantName ? `by ${titleCase(item.attendantName)} · ` : ''}
                    {item.redemptionsUsed}/{item.redemptionLimit} used · {new Date(item.createdAt).toLocaleDateString()}
                    {item.expiresAt ? ` · expires ${new Date(item.expiresAt).toLocaleDateString()}` : ''}
                  </Text>
                </View>
              </View>
            )}
            ListEmptyComponent={<Text style={styles.empty}>No vouchers found</Text>}
          />
        )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  offer: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  badge: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.text, marginTop: 3, fontWeight: fontWeight.medium },
  sub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
